import { useAtom } from '@reatom/npm-react'
import styled, { css } from 'styled-components'

import { Button, Separator } from '@blade/ui-kit'
import { Deeplink, DeeplinkURL } from '@root/entities/deeplinking'
import { InviteCode, referralCodeAtom } from '@root/entities/referral'
import { useTranslation } from '@root/shared/localization'
import { Dialog } from '@root/shared/ui-kit'
import { DialogAnnouncement } from '@root/shared/ui-kit/dialog/dialog-announcement'

import { ReferralCodeActions } from '../common/referral-code-actions'
import { ReferralCodeInfo } from '../common/referral-code-info'
import { ReferralPointsInfo } from '../common/referral-points-info'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const ViewReferralCodeDialog = ({ open, onOpenChange }: Props) => {
  const { t } = useTranslation()

  const [referralCode] = useAtom(referralCodeAtom)
  const handleClose = () => onOpenChange(false)

  if (!referralCode) {
    return undefined
  }

  const referralLink = DeeplinkURL.fromDeeplink(
    new Deeplink.InviteCodeJoin({ inviteCode: InviteCode(referralCode) }),
  )

  return (
    <Root
      withClose
      open={open}
      onOpenChange={onOpenChange}
      describedBy={t('referral.invite_referral')}
    >
      <DialogAnnouncement
        css={css`
          --announcement-header-background: var(--appearanceA-7);
          --announcement-body-background: var(--appearanceA-7);
        `}
        header={
          <Header>
            <Title>{t('referral.your-referral-code')}</Title>
            <SubTitle>{t('referral.view-referral-subtitle')}</SubTitle>
            <ReferralCodeInfo
              referralCode={referralCode}
              withLink
              css={css`
                margin-block: 20px 16px;
              `}
            />
            <ReferralCodeActions referralLink={referralLink} />
            <Separator
              css={css`
                margin-top: 22px;
                margin-bottom: 6px;
              `}
            />
          </Header>
        }
      >
        <Body>
          <ReferralPointsInfo withLabel />
          <ButtonClose variant="soft" radius="rounded" onClick={handleClose}>
            {t('common.close')}
          </ButtonClose>
        </Body>
      </DialogAnnouncement>
    </Root>
  )
}

const Root = styled(Dialog)`
  width: 400px;
`

const Body = styled.div`
  padding: 10px;
  display: grid;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 12px 0;
  position: relative;
  text-align: center;
`

const Title = styled.h2`
  font-weight: 700;
  font-size: 18px;
  line-height: normal;
  line-height: 140%;
`

const SubTitle = styled.h3`
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: var(--textA-9);

  strong {
    color: var(--textA-11);
  }
`

const ButtonClose = styled(Button)`
  margin-top: 18px;
  margin-bottom: 10px;
  font-size: 12px;
`
